import { Component } from '@theme/component';
import '@theme/component-cart-quantity-selector';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

// A section may reconnect while its own HTML is hydrated. Refresh it only once
// per history restoration, including when this module loads after pageshow.
const restoredDrawerSections = new Set();
window.addEventListener('pageshow', (event) => {
  if (event.persisted) restoredDrawerSections.clear();
});

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  #renderNotificationQueued = false;
  /** @type {{ request: CartLinesUpdateEvent, didError: boolean } | undefined} */
  #renderResult;
  #cartMutationRevision = 0;
  /** @type {AbortController | null} */
  #cartRecoveryController = null;
  #cartControlsLocked = false;
  #pendingCartRenders = 0;
  /** @type {AbortController | null} */
  #historyRefreshController = null;
  /** @type {AbortController | null} */
  #historyMorphController = null;

  /** @param {PageTransitionEvent} [event] */
  #restoreDrawerFromHistory = (event) => {
    if (!this.isDrawer || !this.dataset.sectionId) return;
    const navigation = /** @type {PerformanceNavigationTiming | undefined} */ (
      performance.getEntriesByType('navigation')[0]
    );
    if (!event?.persisted && navigation?.type !== 'back_forward') return;
    if (restoredDrawerSections.has(this.sectionId)) return;
    restoredDrawerSections.add(this.sectionId);
    this.#refreshDrawerFromHistory();
  };

  #cancelHistoryRefresh = () => {
    this.#historyRefreshController?.abort();
    this.#historyRefreshController = null;
  };

  async #refreshDrawerFromHistory() {
    if (!this.isConnected || this.#historyRefreshController || this.#cartControlsLocked || this.#pendingCartRenders) return;
    const controller = new AbortController();
    this.#historyRefreshController = controller;
    const revision = this.#cartMutationRevision;

    try {
      const html = await sectionRenderer.getSectionHTML(
        this.sectionId, false, new URL(window.location.href), controller.signal
      );
      if (controller.signal.aborted || !this.isConnected || revision !== this.#cartMutationRevision) return;
      if (!html) throw new Error('Cart restoration returned no section');

      // Hydrate only the drawer's contents. Its dialog and opening state stay
      // owned by theme-drawer; no synthetic add-to-cart event is dispatched.
      this.#historyMorphController = controller;
      await morphSection(this.sectionId, html, { mode: 'hydration', injectStylesheet: true });
      if (!controller.signal.aborted && this.isConnected) this.#notifyRendered();
    } catch (error) {
      if (!controller.signal.aborted && error?.name !== 'AbortError') {
        console.warn('[cart-items] Could not restore cart drawer:', error);
      }
    } finally {
      if (this.#historyRefreshController === controller) this.#historyRefreshController = null;
      if (this.#historyMorphController === controller) this.#historyMorphController = null;
    }
  }

  // Consumers need the rendered cart, not just the completed server mutation.
  // This also covers morphs inside a View Transition and newly inserted components.
  /** @param {{ request: CartLinesUpdateEvent, didError: boolean }} [result] */
  #notifyRendered(result) {
    if (result) this.#renderResult = result;
    if (this.#renderNotificationQueued) return;
    this.#renderNotificationQueued = true;
    queueMicrotask(() => {
      this.#renderNotificationQueued = false;
      const detail = this.#renderResult;
      this.#renderResult = undefined;
      if (!this.isConnected) return;
      this.#updateCartQuantitySelectorButtonStates();
      if (this.#cartControlsLocked) this.#protectCartControls();
      this.dispatchEvent(new CustomEvent('cart:rendered', { bubbles: true, detail }));
    });
  }

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    this.#cancelHistoryRefresh();
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        this.#cancelHistoryRefresh();
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    this.#cancelHistoryRefresh();
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        this.#cancelHistoryRefresh();
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#restoreDrawerFromHistory);
    window.addEventListener('pagehide', this.#cancelHistoryRefresh);
    this.#notifyRendered();
    this.#restoreDrawerFromHistory();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#notifyRendered();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#restoreDrawerFromHistory);
    window.removeEventListener('pagehide', this.#cancelHistoryRefresh);
    if (this.#historyRefreshController && this.#historyRefreshController !== this.#historyMorphController) {
      // A detached drawer with an unfinished read still needs fresh HTML when
      // it reconnects. A reconnect caused by our own morph must not refetch.
      restoredDrawerSections.delete(this.sectionId);
    }
    this.#cancelHistoryRefresh();
    this.#cartRecoveryController?.abort();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    if (this.#cartControlsLocked || this.hasAttribute('data-cart-recovery-required')) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    if (this.#cartControlsLocked || this.hasAttribute('data-cart-recovery-required')) return;

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    this.querySelector('[data-cart-recovery-error]')?.remove();
    // Retain only this cart's presentation for an offline fallback. A successful
    // recovery always replaces it with fresh, authoritative section HTML.
    const snapshot = /** @type {Element} */ (this.cloneNode(true));
    const heading = this.isDrawer ? document.getElementById('cart-drawer-heading') : null;
    const headingParent = heading?.parentNode;
    const headingNext = heading?.nextSibling;
    const removal = {
      active: true,
      isCurrent: () => true,
      restore: () => {
        morph(this, snapshot);
        if (heading && headingParent?.isConnected && !document.getElementById(heading.id)) {
          headingParent.insertBefore(heading, headingNext?.parentNode === headingParent ? headingNext : null);
        }
      },
    };

    this.updateQuantity({ line, quantity: 0, action: 'clear', removal });

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        if (!removal.active || !removal.isCurrent()) return;
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => {
        if (removal.active && removal.isCurrent()) row.remove();
      };

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {{ active: boolean, isCurrent: () => boolean, restore: () => void }} [config.removal]
   */
  updateQuantity(config) {
    if (this.#cartControlsLocked || this.hasAttribute('data-cart-recovery-required')) return;
    this.querySelector('[data-cart-recovery-error]')?.remove();
    const revision = ++this.#cartMutationRevision;
    this.#cartRecoveryController?.abort();
    const isCurrent = () => this.isConnected && revision === this.#cartMutationRevision;
    const { removal } = config;
    if (removal) removal.isCurrent = isCurrent;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    // The page and drawer represent the same cart. Protect both while its
    // quantities are pending, including keyboard and accelerated checkout.
    cartItemsComponents.forEach((item) => {
      if (item instanceof CartItemsComponent) item.#disableCartItems();
    });
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then(async (response) => {
        const parsedResponse = JSON.parse(await response.text());
        if (!response.ok && !parsedResponse.errors) throw new Error('Failed to update cart');
        return parsedResponse;
      })
      .then(async (parsedResponseText) => {
        // A later confirmed mutation owns the cart; never morph an older
        // response over it or restart recovery for the superseded request.
        if (!isCurrent()) {
          deferredUpdatePromise.reject(new Error('Cart update superseded'));
          return;
        }

        resetShimmer(this);

        if (parsedResponseText.errors) {
          if (removal) throw new Error(parsedResponseText.errors);
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        if (removal) removal.active = false;
        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
        if (!isCurrent()) {
          deferredUpdatePromise.reject(new Error('Cart update superseded'));
          return;
        }

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch(async (error) => {
        console.error(error);
        if (removal) removal.active = false;
        if (isCurrent()) {
          // An optimistic removal needs its presentation back before recovery.
          // Other failures retain their unconfirmed input until the server is read.
          removal?.restore();
          this.#disableCartItems();
          resetShimmer(this);
          await this.#recoverCart(sectionsToUpdate, isCurrent);
        }
        deferredUpdatePromise.reject(error);

        if (!isCurrent()) return;
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        if (isCurrent()) {
          for (const cart of document.querySelectorAll('cart-items-component')) {
            if (!(cart instanceof CartItemsComponent) || !sectionsToUpdate.has(cart.sectionId)) continue;
            resetShimmer(cart);
            cart.#enableCartItems();
            cart.#updateCartQuantitySelectorButtonStates();
          }
        }
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * A failed transport may have lost a successful mutation's response. Read the
   * server again rather than treating the restored presentation as cart truth.
   * @param {Set<string>} sectionIds
   * @param {() => boolean} isCurrent
   */
  async #recoverCart(sectionIds, isCurrent) {
    const abortController = new AbortController();
    this.#cartRecoveryController = abortController;
    let recovered = false;
    try {
      const results = await Promise.allSettled(Array.from(sectionIds, async (sectionId) => {
        const cart = Array.from(document.querySelectorAll('cart-items-component'))
          .find((element) => /** @type {HTMLElement} */ (element).dataset.sectionId === sectionId);
        if (!(cart instanceof HTMLElement)) return;
        const html = await sectionRenderer.getSectionHTML(
          sectionId, false, new URL(window.location.href), abortController.signal
        );
        if (!isCurrent() || abortController.signal.aborted) return;
        if (!html) throw new Error('Cart recovery returned no section');
        await morphSection(sectionId, html, {
          mode: cart.hasAttribute('data-drawer') ? 'hydration' : 'full',
          injectStylesheet: true,
        });
      }));
      const failed = results.find((result) => result.status === 'rejected');
      if (failed?.status === 'rejected') throw failed.reason;
      recovered = true;
    } catch (recoveryError) {
      if (!abortController.signal.aborted) console.warn('[cart-items] Cart recovery failed:', recoveryError);
    } finally {
      if (this.#cartRecoveryController === abortController) this.#cartRecoveryController = null;
    }
    if (!isCurrent()) return;

    if (recovered) {
      // Header icons sit outside the drawer's hydration boundary. Reconcile
      // their native count/cache without reporting the failed mutation as a
      // success (which could also restart cart reservation state).
      const itemCount = Number.parseInt(this.querySelector('[ref="cartItemCount"]')?.textContent || '0', 10);
      if (Number.isFinite(itemCount) && itemCount >= 0) {
        for (const icon of document.querySelectorAll('cart-icon')) {
          const cartIcon = /** @type {HTMLElement & { renderCartBubble?: (count: number, animate: boolean) => Promise<void> }} */ (icon);
          cartIcon.renderCartBubble?.(itemCount, false)?.catch((iconError) => {
            console.warn('[cart-items] Cart count recovery failed:', iconError);
          });
        }
      }
    }

    for (const cart of document.querySelectorAll('cart-items-component')) {
      if (!(cart instanceof CartItemsComponent) || !sectionIds.has(cart.sectionId)) continue;
      cart.#showRecoveryError(recovered);
    }
  }

  /** @param {boolean} recovered */
  #showRecoveryError(recovered) {
    this.querySelector('[data-cart-recovery-error]')?.remove();
    resetShimmer(this);
    // The failure is visible even when no line-level error can be rendered.
    const alert = document.createElement('div');
    alert.className = 'cart-item__error cart-primary-typography';
    alert.setAttribute('role', 'alert');
    alert.setAttribute('data-cart-recovery-error', '');
    alert.textContent = recovered
      ? 'Your cart has been refreshed. Please check the quantities before continuing.'
      : 'We couldn’t confirm your cart update. Refresh your cart before continuing.';
    if (!recovered) {
      // The snapshot is explicitly unconfirmed. Prevent another line-number
      // mutation/checkout from using it until the shopper refreshes the cart.
      this.setAttribute('data-cart-recovery-required', '');
      this.#protectCartControls();
      const refresh = document.createElement('a');
      refresh.href = Theme.routes.cart_url;
      refresh.textContent = 'Refresh cart';
      alert.append(' ', refresh);
    }
    this.prepend(alert);
    this.#notifyRendered();
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    this.#cancelHistoryRefresh();
    if (event.target === this) return;
    let ownsRender = false;

    event.promise
      ?.then(async ({ detail }) => {
        this.#cancelHistoryRefresh();
        if (detail?.didError) {
          this.#notifyRendered({ request: event, didError: true });
          return;
        }

        // A confirmed external mutation now owns the rendered cart. A failed
        // add must not cancel recovery of an independent failed removal.
        ownsRender = true;
        this.#pendingCartRenders += 1;
        this.#cartMutationRevision += 1;
        this.#cartRecoveryController?.abort();

        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        // A closed drawer must receive its filled DOM before it can open. Only
        // animate empty → filled when the shopper already sees the open drawer.
        const animateEmptyCart = wasEmptyCartDrawer && this.closest('theme-drawer')?.hasAttribute('open');
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (animateEmptyCart) {
            await startViewTransition(() => morphSection(this.sectionId, cartItemsHtml, morphOptions), [
              'fill-cart-drawer',
            ]);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          this.#updateCartQuantitySelectorButtonStates();
        } else {
          await sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }

        // Associate DOM completion with this exact mutation, including the
        // asynchronous Section Rendering fallback and unchanged DOM responses.
        resetShimmer(this);
        this.#enableCartItems();
        this.#notifyRendered({ request: event, didError: false });
      })
      .catch((error) => {
        // If a newer confirmed mutation superseded recovery but its rendering
        // failed too, keep the checkout protected and expose the refresh path.
        if (ownsRender && this.#cartControlsLocked) {
          this.#showRecoveryError(false);
          this.#enableCartItems();
        }
        this.#notifyRendered({ request: event, didError: true });
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      })
      .finally(() => {
        if (!ownsRender) return;
        this.#pendingCartRenders -= 1;
        this.#enableCartItems();
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.#cartControlsLocked = true;
    // These edits have been submitted. An authoritative response must replace
    // their value even if the input still has focus when the update completes.
    for (const selector of this.refs.quantitySelectors ?? []) {
      selector.querySelector('input')?.removeAttribute('data-skip-value-update');
    }
    this.classList.add('cart-items-disabled');
    this.#protectCartControls();
  }

  /** Disable only controls owned by this update; retain existing restrictions. */
  #protectCartControls() {
    for (const element of this.querySelectorAll('form, [form], .additional-checkout-buttons, shopify-accelerated-checkout-cart')) {
      if (element.hasAttribute('inert')) continue;
      element.setAttribute('data-cart-update-inert', '');
      element.setAttribute('inert', '');
    }
    for (const button of this.querySelectorAll('[name="checkout"]')) {
      if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement) || button.disabled) continue;
      button.setAttribute('data-cart-update-disabled', '');
      button.disabled = true;
    }
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    // A mutation may return only one cart section. Its origin must not unlock
    // another component while that component is still fetching its own render.
    if (this.#pendingCartRenders > 0) {
      this.#cartControlsLocked = true;
      this.#protectCartControls();
      return;
    }
    this.#cartControlsLocked = this.hasAttribute('data-cart-recovery-required');
    this.classList.remove('cart-items-disabled');
    if (this.#cartControlsLocked) {
      this.#protectCartControls();
      return;
    }
    // Freshly rendered server-disabled controls have no marker and stay disabled.
    for (const element of this.querySelectorAll('[data-cart-update-inert]')) {
      element.removeAttribute('inert');
      element.removeAttribute('data-cart-update-inert');
    }
    for (const button of this.querySelectorAll('[data-cart-update-disabled]')) {
      if (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) button.disabled = false;
      button.removeAttribute('data-cart-update-disabled');
    }
  }

  /**
   * Updates product quantity selectors with the total across all lines of each variant.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    const quantitiesByVariant = new Map();
    for (const item of updatedCart.items) {
      const variantId = String(item.variant_id);
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) ?? 0) + item.quantity);
    }

    // Visit the selectors, not just remaining cart lines, so removed variants
    // reset to zero. Cart line selectors keep their own per-line quantities.
    for (const selector of document.querySelectorAll('quantity-selector-component[data-variant-id]')) {
      const input = selector.querySelector('input[data-cart-quantity]');
      if (!input) continue;

      const quantity = quantitiesByVariant.get(selector.getAttribute('data-variant-id')) ?? 0;
      input.setAttribute('data-cart-quantity', String(quantity));

      // Update the quantity selector's internal state
      if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
        selector.updateCartQuantity();
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
